import { Observable } from 'rxjs';
import { KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';

export const enum CalloutType {
    AdminTile = 'AdminTile',
    AppLauncher = 'AppLauncher',
    Benefits = 'Benefits',
    BuyOffice = 'BuyOffice',
    DiscoverTab = 'DiscoverTab',
    DragAndDrop = 'DragAndDrop',
    Edgeworth = 'Edgeworth',
    Feedback = 'Feedback',
    FormsTile = 'FormsTile',
    Install = 'Install',
    LeftNav = 'LeftNav',
    MyAccount = 'MyAccount',
    MyContent = 'MyContent',
    NewButton = 'NewButton',
    NewThemes = 'NewThemes',
    OneDriveTile = 'OneDriveTile',
    OneNoteTile = 'OneNoteTile',
    Recommended = 'Recommended',
    SearchBar = 'SearchBar',
    SharedTab = 'SharedTab',
    TeamsTile = 'TeamsTile',
    Upload = 'Upload',
    Welcome = 'Welcome',
    WordTile = 'WordTile',
    WorkspaceNav = 'WorkspaceNav',
    FormsFeatureCallout = 'FormsFeatureCallout'
}

export const enum FeatureCalloutAction {
    None,
    Close,
    Next,
    Back,
    Teach
}

export class FeatureCallout {

    public onAction: (action: FeatureCalloutAction) => void;
    public completed = false;
    public shouldScroll: boolean;
    public ariaDescription?: string;

    /**
     * Instantiates a new FeatureCallout model
     * @param calloutElement: The element on the page that is being highlighted to the user; the search box, app launcher, install button, etc.
     * @param calloutType: An enum indicating the type of callout being shown
     * @param area: UX area containing calloutElement
     * @param title: The text to be used as the callout title
     * @param subTitle: The text to be used as the callout subtitle
     * @param activationClasses: The classes for the callout element when it is active
     * @param initialClasses: The classes for the callout element initially or when not active
     * @param buttonIdPrefix: A prefix to be prepended to the callout buttons
     * @param persistDismissal: A boolean indicating whether or not the dismissal state should be persisted
     * @param persistCompletion: A boolean indicating whether or not the completion state should be persisted
     * @param inline: Whether the callout should be displayed inline
     * @param imageClass: The class that displays the callout sprite image
     * @param teachingCallout: Whether the callout is a teaching callout
     * @param dismissCalloutObservable: Observable that emits when the callout should be dismissed without user interaction
     * @param horizontalOffsetOverride: Optional param that contains the horizontal offset value
     * @param verticalOffsetOverride: Optional param that contains the vertical offset value
     * @param footer: The text to be used as the callout footer
     * @param arrowDisplacementOffset: Optional param that contains a displacement value for the beak
     * @param ariaDescription: Optional param for the aria description of a callout
     * @param centered: Optional param for centering the callout and having no beaks (used in the returning user callouts)
     */
    constructor(
        public calloutElement: Element,
        public calloutType: CalloutType,
        public area: string,
        public title: string,
        public subTitle: string,
        public activationClasses: Array<string> = [],
        public initialClasses: Array<string> = [],
        public buttonIdPrefix: string = 'callout',
        public persistDismissal: boolean = false,
        public persistCompletion: boolean = false,
        public inline: boolean = false,
        public imageClass: string = '',
        public teachingCallout: boolean = false,
        public dismissCalloutObservable: Observable<any> = null,
        public horizontalOffsetOverride?: number,
        public verticalOffsetOverride?: number,
        public footer?: string,
        public arrowDisplacementOffset?: number,
        public svgImage?: string,
        public calloutOnTop?: boolean,
        public centered: boolean = false,
        public instrumentImpressionEvent?: KnownInstrumentationEvents
    ) {
        this.shouldScroll = this.checkScroll();
    }

    public isValid(): boolean {
        return (this.calloutElement || this.centered) && (!!this.subTitle || !!this.title);
    }

    private checkScroll(): boolean {
        switch (this.calloutType) {
            case CalloutType.BuyOffice:
            case CalloutType.DiscoverTab:
            case CalloutType.DragAndDrop:
            case CalloutType.Edgeworth:
            case CalloutType.Feedback:
            case CalloutType.FormsFeatureCallout:
            case CalloutType.Install:
            case CalloutType.MyAccount:
            case CalloutType.Recommended:
            case CalloutType.SharedTab:
            case CalloutType.Upload:
                return true;
            default:
                return false;
        }
    }
}
